// @ts-nocheck — migrated from JS; reactstrap / react-select / layout prop friction.
import React, { Fragment, useMemo } from "react";
import IntlMessages from "Util/IntlMessages";
import { useIntl } from "react-intl";
import {
  Row,
  Card,
  CardBody,
  CardTitle,
  Badge,
  Button,
  Progress
} from "reactstrap";
import { Colxx, Separator } from "Components/CustomBootstrap";
import BreadcrumbContainer from "Components/BreadcrumbContainer";
import PerfectScrollbar from "react-perfect-scrollbar";
import { PERFECT_SCROLLBAR_MAX_88 } from "Constants/perfectScrollbarLayout";
import ReactTable from "react-table";
import DataTablePagination from "Components/DataTables/pagination";
import Chart from "react-apexcharts";
import { Sparklines, SparklinesLine } from "react-sparklines";
import { useKpiSparklineLineStyle } from "Hooks/useKpiSparklineLineStyle";
import { ReactifyChartColors } from "Util/ReactifyChartColors";
import { addCommas } from "Util/Utils";
import data from "Data/dashboardEducation.json";

import "react-table/react-table.css";

function EducationDashboard() {
  const colors = useMemo(() => ReactifyChartColors(), []);
  const kpiSparkLineStyle = useKpiSparklineLineStyle(colors.ReactifyChartColor1);
  const intl = useIntl();

  const funnelOptions = useMemo(() => {
    const c = colors;
    return {
      chart: { toolbar: { show: false } },
      plotOptions: {
        bar: {
          horizontal: true,
          barHeight: "78%",
          distributed: true,
          borderRadius: 4
        }
      },
      colors: [
        c.ReactifyChartColor1,
        c.ReactifyChartColor2,
        c.ReactifyChartColor3,
        c.ReactifyChartColor4,
        c.ReactifyChartColor5
      ],
      dataLabels: {
        enabled: true,
        formatter: (val, ctx) => {
          const i = ctx.dataPointIndex;
          if (i === 0) return addCommas(val);
          const prev = data.courseFunnel[i - 1].value;
          const pct = Math.round((val / prev) * 1000) / 10;
          return `${addCommas(val)} (${pct}%)`;
        }
      },
      xaxis: {
        categories: data.courseFunnel.map(f => f.step),
        labels: { style: { colors: c.primaryColor } }
      },
      grid: { borderColor: c.separatorColor },
      legend: { show: false },
      tooltip: { theme: "light" }
    };
    }, [colors]);

  const enrollOptions = useMemo(() => {
    const c = colors;
    return {
      chart: { toolbar: { show: false }, zoom: { enabled: false } },
      stroke: { width: [3, 3], curve: "smooth" },
      colors: [c.ReactifyChartColor1, c.ReactifyChartColor3],
      dataLabels: { enabled: false },
      legend: { position: "top" },
      xaxis: {
        categories: data.enrollmentTrend.months,
        labels: { style: { colors: c.primaryColor } }
      },
      yaxis: {
        labels: {
          style: { colors: c.primaryColor },
          formatter: v => addCommas(v)
        }
      },
      grid: { borderColor: c.separatorColor, strokeDashArray: 4 },
      tooltip: { theme: "light" }
    };
    }, [colors]);

  const activityOptions = useMemo(() => {
    const c = colors;
    return {
      chart: { toolbar: { show: false } },
      plotOptions: { bar: { borderRadius: 4, columnWidth: "60%" } },
      colors: [c.ReactifyChartColor2],
      dataLabels: { enabled: false },
      xaxis: {
        categories: data.activityHeat.days,
        labels: { style: { colors: c.primaryColor } }
      },
      yaxis: {
        labels: {
          style: { colors: c.primaryColor },
          formatter: v => addCommas(v)
        }
      },
      grid: { borderColor: c.separatorColor },
      tooltip: { theme: "light" }
    };
    }, [colors]);

  const segmentOptions = useMemo(() => {
    const c = colors;
    return {
      labels: data.learnerSegments.map(s => s.label),
      colors: [
        c.ReactifyChartColor1,
        c.ReactifyChartColor2,
        c.ReactifyChartColor3,
        c.ReactifyChartColor4
      ],
      legend: { position: "bottom" },
      plotOptions: { pie: { donut: { size: "62%" } } },
      dataLabels: { enabled: true }
    };
    }, [colors]);

  const coursesColumns = useMemo(() => {
    const { formatMessage } = intl;
    return [
      {
        Header: formatMessage({ id: "edu.col.course" }),
        accessor: "title",
        Cell: p => <span className="font-weight-medium">{p.value}</span>
      },
      {
        Header: formatMessage({ id: "edu.col.enrolled" }),
        accessor: "enrolled",
        Cell: p => addCommas(p.value)
      },
      {
        Header: formatMessage({ id: "edu.col.completion" }),
        accessor: "completionPct",
        Cell: p => `${p.value}%`
      },
      {
        Header: formatMessage({ id: "edu.col.rating" }),
        accessor: "rating",
        Cell: p => (
          <span>
            <i className="fa fa-star text-warning me-1" />
            {p.value}
          </span>
        )
      }
    ];
    }, [intl]);

  const instructorColumns = useMemo(() => {
    const { formatMessage } = intl;
    return [
      {
        Header: formatMessage({ id: "edu.col.instructor" }),
        accessor: "name",
        Cell: p => <span className="font-weight-medium">{p.value}</span>
      },
      { Header: formatMessage({ id: "edu.col.courses" }), accessor: "courses" },
      {
        Header: formatMessage({ id: "edu.col.learners" }),
        accessor: "learners",
        Cell: p => addCommas(p.value)
      },
      {
        Header: formatMessage({ id: "edu.col.rating" }),
        accessor: "rating",
        Cell: p => (
          <span>
            <i className="fa fa-star text-warning me-1" />
            {p.value}
          </span>
        )
      }
    ];
    }, [intl]);

  const k = data.kpis;

  return (
      <div className="dashboard-vertical dashboard-vertical--education">
        <Fragment>
          <Row>
            <Colxx xxs="12">
              <BreadcrumbContainer
                heading={<IntlMessages id="menu.education" />}
              />
              <Separator className="mb-5" />
            </Colxx>
          </Row>

          <Row className="mb-2">
            <Colxx lg="8" className="mb-3">
              <Card className="border-primary border-opacity-25 bg-light">
                <CardBody className="py-3 d-flex flex-wrap justify-content-between align-items-center">
                  <div>
                    <h6 className="mb-1">
                      <IntlMessages id="edu.hero.title" />
                    </h6>
                    <p className="text-muted small mb-0">
                      <IntlMessages id="edu.hero.sub" />
                    </p>
                  </div>
                  <Button color="primary" size="sm" className="mt-2 mt-lg-0">
                    <IntlMessages id="edu.hero.cohort" />
                  </Button>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx lg="4" className="mb-3">
              <Card className="border-primary border-opacity-25 bg-light h-100">
                <CardBody className="py-3">
                  <p className="text-muted mb-2 small text-uppercase">
                    <IntlMessages id="edu.section.news" />
                  </p>
                  <PerfectScrollbar style={PERFECT_SCROLLBAR_MAX_88}>
                    <ul className="list-unstyled mb-0 small">
                      {data.announcements.map((a, i) => (
                        <li key={i} className="mb-2">
                          <span className="text-muted me-2">{a.date}</span>
                          {a.text}
                        </li>
                      ))}
                    </ul>
                  </PerfectScrollbar>
                </CardBody>
              </Card>
            </Colxx>
          </Row>

          <Row>
            <Colxx sm="6" md="4" lg="2" className="mb-4">
              <Card className="h-100 dashboard-vertical__kpi-card">
                <CardBody>
                  <Sparklines data={data.learnerSpark} margin={6}>
                    <SparklinesLine style={kpiSparkLineStyle} />
                  </Sparklines>
                  <p className="text-muted mb-1 small text-uppercase">
                    <IntlMessages id="edu.kpi.learners" />
                  </p>
                  <h4 className="mb-0">{addCommas(k.activeLearners)}</h4>
                  <span className="text-success small">+{k.learnersDelta}%</span>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx sm="6" md="4" lg="2" className="mb-4">
              <Card className="h-100 dashboard-vertical__kpi-card">
                <CardBody>
                  <i className="fa fa-book text-primary mb-2 d-block" />
                  <p className="text-muted mb-1 small text-uppercase">
                    <IntlMessages id="edu.kpi.courses" />
                  </p>
                  <h4 className="mb-0">{k.coursesLive}</h4>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx sm="6" md="4" lg="2" className="mb-4">
              <Card className="h-100 dashboard-vertical__kpi-card">
                <CardBody>
                  <i className="fa fa-tasks text-success mb-2 d-block" />
                  <p className="text-muted mb-1 small text-uppercase">
                    <IntlMessages id="edu.kpi.completion" />
                  </p>
                  <h4 className="mb-0">{k.completionRatePct}%</h4>
                  <span className="text-success small">+{k.completionDelta}%</span>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx sm="6" md="4" lg="2" className="mb-4">
              <Card className="h-100 dashboard-vertical__kpi-card">
                <CardBody>
                  <i className="fa fa-certificate text-warning mb-2 d-block" />
                  <p className="text-muted mb-1 small text-uppercase">
                    <IntlMessages id="edu.kpi.certs" />
                  </p>
                  <h4 className="mb-0">{addCommas(k.certificates30d)}</h4>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx sm="6" md="4" lg="2" className="mb-4">
              <Card className="h-100 dashboard-vertical__kpi-card">
                <CardBody>
                  <i className="fa fa-graduation-cap text-info mb-2 d-block" />
                  <p className="text-muted mb-1 small text-uppercase">
                    <IntlMessages id="edu.kpi.score" />
                  </p>
                  <h4 className="mb-0">{k.avgScorePct}%</h4>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx sm="6" md="4" lg="2" className="mb-4">
              <Card className="h-100 dashboard-vertical__kpi-card">
                <CardBody>
                  <i className="fa fa-smile-o text-secondary mb-2 d-block" />
                  <p className="text-muted mb-1 small text-uppercase">
                    <IntlMessages id="edu.kpi.nps" />
                  </p>
                  <h4 className="mb-0">{k.nps}</h4>
                  <p className="text-muted small mb-0">
                    <IntlMessages id="edu.kpi.revenue" /> $
                    {addCommas(k.revenueMtd)}
                  </p>
                </CardBody>
              </Card>
            </Colxx>
          </Row>

          <Row>
            <Colxx lg="7" className="mb-4">
              <Card className="h-100 dashboard-vertical__chart-card">
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="edu.chart.enrollment" />
                  </CardTitle>
                  <Chart
                    options={enrollOptions}
                    series={[
                      {
                        name: "Enrollments",
                        data: data.enrollmentTrend.enrollments
                      },
                      {
                        name: "Completions",
                        data: data.enrollmentTrend.completions
                      }
                    ]}
                    type="line"
                    height={340}
                    width="100%"
                  />
                </CardBody>
              </Card>
            </Colxx>
            <Colxx lg="5" className="mb-4">
              <Card className="h-100 dashboard-vertical__chart-card">
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="edu.chart.funnel" />
                  </CardTitle>
                  <Chart
                    options={funnelOptions}
                    series={[{ data: data.courseFunnel.map(f => f.value) }]}
                    type="bar"
                    height={340}
                    width="100%"
                  />
                </CardBody>
              </Card>
            </Colxx>
          </Row>

          <Row>
            <Colxx lg="4" className="mb-4">
              <Card className="dashboard-vertical__chart-card">
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="edu.chart.activity" />
                  </CardTitle>
                  <Chart
                    options={activityOptions}
                    series={[
                      { name: "Hours", data: data.activityHeat.hours }
                    ]}
                    type="bar"
                    height={280}
                    width="100%"
                  />
                </CardBody>
              </Card>
            </Colxx>
            <Colxx lg="4" className="mb-4">
              <Card className="h-100 dashboard-vertical__chart-card">
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="edu.chart.segments" />
                  </CardTitle>
                  <Chart
                    options={segmentOptions}
                    series={data.learnerSegments.map(s => s.value)}
                    type="donut"
                    height={280}
                    width="100%"
                  />
                </CardBody>
              </Card>
            </Colxx>
            <Colxx lg="4" className="mb-4">
              <Card className="h-100">
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="edu.section.cohorts" />
                  </CardTitle>
                  {data.cohorts.map((c, i) => (
                    <div key={i} className="mb-3 pb-3 border-bottom">
                      <div className="d-flex justify-content-between">
                        <span className="font-weight-medium">{c.name}</span>
                        <Badge color="info">{c.due}</Badge>
                      </div>
                      <div className="text-muted small">
                        {addCommas(c.learners)}{" "}
                        <IntlMessages id="edu.cohort.learners" />
                      </div>
                      <div className="d-flex align-items-center mt-1">
                        <span className="small me-2">
                          {c.activePct}%{" "}
                          <IntlMessages id="edu.cohort.active" />
                        </span>
                        <Progress value={c.activePct} className="flex-grow-1 mb-0" />
                      </div>
                    </div>
                  ))}
                </CardBody>
              </Card>
            </Colxx>
          </Row>

          <Row>
            <Colxx lg="6" className="mb-4">
              <Card>
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="edu.table.courses" />
                  </CardTitle>
                  <ReactTable
                    defaultPageSize={5}
                    data={data.topCourses}
                    columns={coursesColumns}
                    minRows={0}
                    PaginationComponent={DataTablePagination}
                  />
                </CardBody>
              </Card>
            </Colxx>
            <Colxx lg="6" className="mb-4">
              <Card>
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="edu.table.instructors" />
                  </CardTitle>
                  <ReactTable
                    defaultPageSize={5}
                    data={data.instructors}
                    columns={instructorColumns}
                    minRows={0}
                    PaginationComponent={DataTablePagination}
                  />
                </CardBody>
              </Card>
            </Colxx>
          </Row>
        </Fragment>
      </div>
    );
}

export default EducationDashboard;
